/* global axios */
import ApiClient from './ApiClient';

/**
 * OCHAT: cliente de la configuración del hotel.
 *
 * Va separado de `api/bookings.js` porque son dos audiencias distintas: ese cliente
 * sirve a la operación diaria (y su API la consume el agente IA desde n8n), este
 * sirve a la pantalla que el dueño del hotel usa para cargar una temporada.
 *
 * POR QUÉ NO ALCANZA ApiClient SOLO
 * Los tipos de habitación cuelgan de la propiedad y los planes de tarifa cuelgan
 * del tipo de habitación, así que las rutas anidadas no encajan en el molde de
 * `resource` único de ApiClient. Se arman sobre `baseUrl()`, que es lo que resuelve
 * el `/accounts/:id` desde la URL del navegador.
 */
class BookingSettingsAPI extends ApiClient {
  constructor() {
    super('booking_properties', { accountScoped: true });
  }

  // --------------------------------------------------------------- propiedades

  /** Cada propiedad viene con sus tipos de habitación y planes anidados. */
  properties() {
    return axios.get(this.url);
  }

  property(id) {
    return axios.get(`${this.url}/${id}`);
  }

  createProperty(property) {
    return axios.post(this.url, { property });
  }

  updateProperty(id, property) {
    return axios.patch(`${this.url}/${id}`, { property });
  }

  deleteProperty(id) {
    return axios.delete(`${this.url}/${id}`);
  }

  // ---------------------------------------------------- tipos de habitación

  roomTypes(propertyId) {
    return axios.get(`${this.url}/${propertyId}/booking_room_types`);
  }

  createRoomType(propertyId, roomType) {
    return axios.post(`${this.url}/${propertyId}/booking_room_types`, {
      room_type: roomType,
    });
  }

  updateRoomType(propertyId, id, roomType) {
    return axios.patch(`${this.url}/${propertyId}/booking_room_types/${id}`, {
      room_type: roomType,
    });
  }

  deleteRoomType(propertyId, id) {
    return axios.delete(`${this.url}/${propertyId}/booking_room_types/${id}`);
  }

  // ------------------------------------------------------- planes de tarifa

  /**
   * Cuelgan del tipo de habitación, no de la propiedad: el mismo hotel vende la
   * doble con desayuno y la suite sin, y cada tipo tiene sus propios planes.
   */
  get ratePlansRoot() {
    return `${this.baseUrl()}/booking_room_types`;
  }

  ratePlans(roomTypeId) {
    return axios.get(`${this.ratePlansRoot}/${roomTypeId}/booking_rate_plans`);
  }

  createRatePlan(roomTypeId, ratePlan) {
    return axios.post(
      `${this.ratePlansRoot}/${roomTypeId}/booking_rate_plans`,
      {
        rate_plan: ratePlan,
      }
    );
  }

  updateRatePlan(roomTypeId, id, ratePlan) {
    return axios.patch(
      `${this.ratePlansRoot}/${roomTypeId}/booking_rate_plans/${id}`,
      { rate_plan: ratePlan }
    );
  }

  /**
   * El servidor decide si borra o desactiva: si ya se vendió con este plan, lo
   * desactiva y devuelve el plan. Por eso la respuesta hay que mirarla.
   */
  deleteRatePlan(roomTypeId, id) {
    return axios.delete(
      `${this.ratePlansRoot}/${roomTypeId}/booking_rate_plans/${id}`
    );
  }

  // ------------------------------------------------------- carga por rango

  /**
   * PUT y no POST porque es idempotente: aplicar "del 1/12 al 28/02, viernes y
   * sábados, 8 habitaciones" dos veces deja el mismo resultado.
   *
   * `inventory` lleva SOLO los campos que el formulario quiere cambiar. El servicio
   * acota el update a esas columnas, así que mandar el precio sin el cupo no pisa
   * el cupo que ya estaba cargado.
   */
  applyInventory(propertyId, { roomTypeId, from, to, weekdays, inventory }) {
    return axios.put(`${this.url}/${propertyId}/calendar/inventory`, {
      room_type_id: roomTypeId,
      from,
      to,
      weekdays,
      inventory,
    });
  }

  applyRates(propertyId, { ratePlanId, from, to, weekdays, rates }) {
    return axios.put(`${this.url}/${propertyId}/calendar/rates`, {
      rate_plan_id: ratePlanId,
      from,
      to,
      weekdays,
      rates,
    });
  }
}

export default new BookingSettingsAPI();
